import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  TextField,
  ListItemText,
  IconButton,
  Tooltip
} from '@material-ui/core';
import { SettingsBackupRestore } from '@material-ui/icons';
import WebFont from 'webfontloader';
import { useFormikContext } from 'formik';
import { TrackingSettingsFormValues } from './tracking-settings';

const googleFonts = [
  'Inter',
  'Lato',
  'Montserrat',
  'Mulish',
  'Nunito',
  'Open Sans',
  'Poppins',
  'Raleway',
  'Roboto',
  'Source Sans Pro',
  'Ubuntu',
  'Work Sans'
];

export default function AppearanceSettings({ reset }: { reset: () => void }) {
  const { values, setFieldValue } = useFormikContext<TrackingSettingsFormValues>();

  const font = values.appearance.font;
  const backgroundColor = values.appearance.background;
  const textPrimary = values.appearance.text_primary;
  const textSecondary = values.appearance.text_secondary;
  const textAccent = values.appearance.text_accent;

  useEffect(() => {
    if (font) {
      WebFont.load({
        google: {
          families: [`${font}:300,400,700`]
        }
      });
    }
  }, [font]);

  return (
    <Box display="flex" flexDirection="column" style={{ padding: 16 }}>
      <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="subtitle1">Appearance</Typography>
          <ListItemText secondary="Customise the look and feel of your customer tracking page." />
        </Box>
        <Tooltip title="Reset to default">
          <IconButton onClick={reset}>
            <SettingsBackupRestore fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box display="flex" flexDirection="column" marginTop={1} style={{ gap: '20px' }}>
        <FormControl fullWidth>
          <InputLabel>Font</InputLabel>
          <Select
            value={font}
            label="Font"
            onChange={(e) => setFieldValue('appearance.font', e.target.value)}
          >
            {googleFonts.map((f) => (
              <MenuItem key={f} value={f} style={{ fontFamily: f }}>
                {f}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box display="flex" flexDirection="row" style={{ gap: '16px' }}>
          <TextField
            label="Background Color"
            type="color"
            fullWidth
            value={backgroundColor}
            onChange={(e) => setFieldValue('appearance.background', e.target.value)}
            InputProps={{ disableUnderline: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              style: {
                width: '100%',
                height: '50px',
                padding: 0,
                border: 'none',
                cursor: 'pointer'
              }
            }}
          />
          <TextField
            label="Headings Color"
            type="color"
            fullWidth
            value={textPrimary}
            onChange={(e) => setFieldValue('appearance.text_primary', e.target.value)}
            InputProps={{ disableUnderline: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              style: {
                width: '100%',
                height: '50px',
                padding: 0,
                border: 'none',
                cursor: 'pointer'
              }
            }}
          />
        </Box>

        <Box display="flex" flexDirection="row" style={{ gap: '16px' }}>
          <TextField
            label="Sub-headings Color"
            type="color"
            fullWidth
            value={textSecondary}
            onChange={(e) => setFieldValue('appearance.text_secondary', e.target.value)}
            InputProps={{ disableUnderline: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              style: {
                width: '100%',
                height: '50px',
                padding: 0,
                border: 'none',
                cursor: 'pointer'
              }
            }}
          />

          <TextField
            label="Highlights Color"
            type="color"
            fullWidth
            value={textAccent}
            onChange={(e) => setFieldValue('appearance.text_accent', e.target.value)}
            InputProps={{ disableUnderline: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              style: {
                width: '100%',
                height: '50px',
                padding: 0,
                border: 'none',
                cursor: 'pointer'
              }
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
